/**
 * Pure, framework-agnostic composition of browser-tab titles.
 *
 * The single source of truth for how a page's leaf label is joined to a
 * brand. Imported by both the `useDocumentTitle` hook (client side) and the
 * root layout's `metadata` template, so the `· <brand>` shape lives in
 * exactly one place.
 *
 * No React import — unit-testable in isolation (CLAUDE.md: keep utilities
 * pure).
 */

/** Internal / admin product brand. */
export const SITE_NAME = "Billdozer"

/** Customer-facing brand for the pay / installment self-service pages. */
export const CUSTOMER_BRAND = "ELVTR"

/** Separator between the page label and the trailing brand. */
export const TITLE_SEPARATOR = " · "

/**
 * Compose a browser-tab title from a page label and a brand.
 *
 * Leaf-first (`"<label> · <brand>"`) so the distinguishing part stays
 * visible when the browser truncates the end of the tab. When `label` is
 * null/undefined/blank the bare `brand` is returned, so a page never
 * regresses to a worse title than the brand alone while its data loads.
 *
 * Args:
 *   label: The page-specific leaf, e.g. "Invoices" or "Invoice #123".
 *     Leading/trailing whitespace is trimmed; blank or absent yields the
 *     bare brand.
 *   brand: The trailing brand. Defaults to `SITE_NAME` (Billdozer);
 *     customer-facing pages pass `CUSTOMER_BRAND` (ELVTR).
 *
 * Returns:
 *   The composed tab title.
 */
export function formatPageTitle(
  label?: string | null,
  brand: string = SITE_NAME,
): string {
  const trimmed = label?.trim()
  return trimmed ? `${trimmed}${TITLE_SEPARATOR}${brand}` : brand
}
